import React from "react";
import { PageTitle } from "./PageTitle";
import Box from "@mui/material/Box";
import { LoadingIndicator } from "./LoadingIndicator";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import { Column } from "./Column";
import { Row } from "./Row";

interface BoardProps {
  title: string;
  subtitle?: React.ReactNode;
  header?: React.ReactNode;
  context?: { provider: React.Provider<Record<string, any>>; value: Record<string, any> };
  isLoading?: boolean;
  variant?: "mobileScrollable";
  children: React.ReactNode;
}
const Board = ({ title, subtitle, header, isLoading = false, context, variant, children }: BoardProps) => {
  const isMobileScrollable = variant === "mobileScrollable";

  return (
    // Because each column has a margin of 5px to the left and to right, but we don't want that margin
    // on the left of the first column/the right of the last column, we use a margin of -5px here ...
    <BoardWrapper context={context}>
      <Box sx={{ mx: "-5px" }}>
        <Row>
          <Column>
            <PageTitle>{title}</PageTitle>
            {subtitle && <Box sx={{ mt: "-25px", mb: "15px" }}>{subtitle}</Box>}
          </Column>
          <Column>{header ?? null}</Column>
        </Row>
        <Box
          aria-label={isMobileScrollable ? `${title}: Spalten` : undefined}
          role={isMobileScrollable ? "region" : undefined}
          sx={
            isMobileScrollable
              ? {
                  maxWidth: "100%",
                  overflowX: { xs: "auto", sm: "visible" },
                  overscrollBehaviorX: "contain",
                }
              : undefined
          }
        >
          <Box
            display="flex"
            sx={{
              mt: "10px",
              ...(isMobileScrollable
                ? {
                    "& > [data-board-column]": {
                      flex: { xs: "0 0 280px", sm: "1 1 0%" },
                    },
                  }
                : {}),
            }}
          >
            {isLoading ? <LoadingIndicator /> : children}
          </Box>
        </Box>
      </Box>
    </BoardWrapper>
  );
};

const BoardWrapper = ({
  context,
  children,
}: {
  context?: { provider: React.Provider<Record<string, any>>; value: Record<string, any> };
  children: React.ReactNode;
}) => (context ? <context.provider value={context.value}>{children}</context.provider> : <>{children}</>);

interface ColumnProps {
  title: string;
  headerHeight?: number;
  headerRef?: React.Ref<any>;
  footer?: React.ReactNode;
  children: React.ReactNode;
}
Board.Column = ({ title, headerHeight, headerRef, footer, children }: ColumnProps) => {
  return (
    <Column
      data-board-column
      sx={{
        m: "0 5px",
        borderRadius: "8px",
        minHeight: "calc(100vh - 260px)",
        flex: 1,
        paddingTop: "15px",
        paddingBottom: "15px",
        bgcolor: "action.hover",
      }}
    >
      <Typography
        ref={headerRef}
        align="center"
        variant="subtitle1"
        sx={{
          color: "text.secondary",
          fontSize: "14px",
          pb: "10px",
          lineHeight: 1.43,
          ...(headerHeight ? { height: `${headerHeight}px` } : {}),
        }}
      >
        {title}
      </Typography>
      <Column
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "5px 20px",
        }}
      >
        {children}
        {footer && <Column sx={{ mt: "auto", width: "100%" }}>{footer}</Column>}
      </Column>
    </Column>
  );
};

interface CardProps {
  onClick: () => void;
  children: React.ReactNode;
}
Board.Card = ({ onClick, children }: CardProps) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: "pointer",
        borderRadius: "12px",
        boxShadow: "var(--backoffice-shadow) 0 2px 8px",
      }}
      elevation={0}
    >
      <Column padding={1.5} position="relative">
        {children}
      </Column>
    </Card>
  );
};

export default Board;
